import { JSX } from "preact"
import FilterSidebar from "@/components/FilterSidebar/FilterSidebar"
import { useNostoAppState } from "@nosto/search-js/preact/hooks"
import { useEffect } from "preact/hooks"
import styles from "./ContentWrapper.module.css"
import { cl } from "@nosto/search-js/utils"

export type ContentChildrenProps = { loading: boolean; foundProducts: boolean }

type ContentWrapperProps = {
  type: string
  children: (props: ContentChildrenProps) => JSX.Element | null
}

const SERP_STATE_ATTR = "data-nosto-serp-state"

function getSerpState(initialized: boolean, loading: boolean, foundProducts: boolean) {
  if (!initialized || loading) return "loading"
  return foundProducts ? "ready" : "empty"
}

/**
 * ContentWrapper takes a function to render non-empty results in the content area.
 * On loading nothing is shown and for empty results the NoResults component is used.
 */
function ContentWrapper({ type, children }: ContentWrapperProps) {
  const { foundProducts, loading, initialized } = useNostoAppState(state => ({
    foundProducts: (state.response.products?.total || 0) > 0,
    loading: state.loading,
    initialized: state.initialized
  }))
  const serpState = getSerpState(initialized, loading, foundProducts)

  useEffect(() => {
    const serp = document.querySelector<HTMLElement>("#serp")
    if (!serp) return

    serp.setAttribute(SERP_STATE_ATTR, serpState)

    return () => {
      serp.removeAttribute(SERP_STATE_ATTR)
    }
  }, [serpState])

  if (!initialized) {
    return null
  }

  return (
    <div className={cl(styles.wrapper, !foundProducts && styles.noResults)} data-nosto-element={type}>
      {foundProducts && <FilterSidebar />}
      <div className={cl(styles.container, loading && styles.loading)}>{children({ loading, foundProducts })}</div>
    </div>
  )
}

export function wrapContent(type: string, Component: ContentWrapperProps["children"]) {
  // eslint-disable-next-line react/display-name
  return () => <ContentWrapper type={type}>{Component}</ContentWrapper>
}
